import React, { useState, useEffect } from 'react';
import { useFer } from '../utils/fer';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Pagination, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Chip, Grid,
} from '@mui/material';
import { ChevronLeft, ChevronRight, DeleteOutline, EditOutlined, Visibility as ViewIcon, AssignmentOutlined, FactoryOutlined, GroupsOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReportBody, ReportActions } from '../components/reports/ReportView';
import { stockAPI } from '../services/api';
import Layout from '../components/Layout';
import { Kpi, Panel, Toolbar } from '../components/ui';
import { tokens } from '../theme';
import { normalizeReport, reportNumber } from '../utils/report';

// Périodes : semaine (lundi → dimanche), mois, période libre, tout
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const bounds = (mode, ref) => {
  const d = new Date(`${ref}T12:00:00`);
  if (mode === 'semaine') {
    const lundi = new Date(d); lundi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const dimanche = new Date(lundi); dimanche.setDate(lundi.getDate() + 6);
    return { start: iso(lundi), end: iso(dimanche) };
  }
  if (mode === 'mois') return { start: iso(new Date(d.getFullYear(), d.getMonth(), 1)), end: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  return null;
};
const shiftRef = (mode, ref, n) => {
  const d = new Date(`${ref}T12:00:00`);
  if (mode === 'semaine') d.setDate(d.getDate() + 7 * n); else d.setMonth(d.getMonth() + n, 1);
  return iso(d);
};
const frLong = (x, opts) => new Date(`${x}T12:00:00`).toLocaleDateString('fr-FR', opts);
const periodLabel = (mode, b) => {
  if (mode === 'mois') return frLong(b.start, { month: 'long', year: 'numeric' });
  const sameMonth = b.start.slice(0, 7) === b.end.slice(0, 7);
  return `Semaine du ${frLong(b.start, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'long' })} au ${frLong(b.end, { day: 'numeric', month: 'long', year: 'numeric' })}`;
};

const ReportsHistory = () => {
  useFer(); // poids des barres pour l'équivalent en kg
  const navigate = useNavigate();
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [info, setInfo] = useState(location.state?.info || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('mois');
  const [ref, setRef] = useState(iso(new Date()));
  const [libre, setLibre] = useState({ start_date: '', end_date: '' });
  const [resume, setResume] = useState(null);
  const b = bounds(mode, ref);
  const filters = b ? { start_date: b.start, end_date: b.end } : mode === 'periode' ? libre : { start_date: '', end_date: '' };
  const filtersKey = `${filters.start_date}|${filters.end_date}`;
  useEffect(() => { setPagination((p) => ({ ...p, page: 1 })); }, [filtersKey]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadReports();
  }, [pagination.page, filtersKey]); // eslint-disable-line

  const loadReports = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });
      
      const response = await stockAPI.getReports(params);
      setReports(response.data.reports);
      setResume(response.data.resume || null);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      setError('Erreur lors du chargement des rapports');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewReport = async (reportId) => {
    try {
      const response = await stockAPI.getReport(reportId);
      setSelectedReport(response.data);
      setOpenDialog(true);
    } catch (error) {
      setError('Erreur lors du chargement du rapport');
    }
  };

  const deleteReport = async (reportId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      try {
        await stockAPI.deleteReport(reportId);
        setError('');
        loadReports();
      } catch (error) {
        setError(error.response?.data?.error || 'Erreur lors de la suppression du rapport');
      }
    }
  };



  return (
    <Layout title="Historique des rapports" plain>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Toolbar>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)} aria-label="Période">
          <ToggleButton value="semaine">Semaine</ToggleButton>
          <ToggleButton value="mois">Mois</ToggleButton>
          <ToggleButton value="periode">Période</ToggleButton>
          <ToggleButton value="tout">Tout</ToggleButton>
        </ToggleButtonGroup>
        {b && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" aria-label="Période précédente" onClick={() => setRef(shiftRef(mode, ref, -1))}><ChevronLeft /></IconButton>
            <Typography sx={{ fontWeight: 600, minWidth: { sm: 250 }, textAlign: 'center', '&::first-letter': { textTransform: 'uppercase' } }}>{periodLabel(mode, b)}</Typography>
            <IconButton size="small" aria-label="Période suivante" onClick={() => setRef(shiftRef(mode, ref, 1))}><ChevronRight /></IconButton>
            <Button size="small" onClick={() => setRef(iso(new Date()))}>Aujourd'hui</Button>
          </Box>
        )}
        {mode === 'periode' && (
          <>
            <TextField type="date" size="small" label="Du" value={libre.start_date} InputLabelProps={{ shrink: true }}
              onChange={(e) => setLibre((prev) => ({ ...prev, start_date: e.target.value }))} />
            <TextField type="date" size="small" label="Au" value={libre.end_date} InputLabelProps={{ shrink: true }}
              onChange={(e) => setLibre((prev) => ({ ...prev, end_date: e.target.value }))} />
          </>
        )}
      </Toolbar>

      {resume && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4} md={3}><Kpi compact icon={AssignmentOutlined} tint="navy" label="Rapports" value={resume.rapports} sub={mode === 'tout' ? 'Depuis le début' : 'Sur la période'} /></Grid>
          <Grid item xs={4} md={3}><Kpi compact icon={FactoryOutlined} tint="navy" label="PBA produits" value={resume.total_pba} sub={resume.rapports ? `${Math.round(resume.total_pba / resume.rapports)} par rapport en moyenne` : '—'} /></Grid>
          <Grid item xs={4} md={3}><Kpi compact icon={GroupsOutlined} tint="gray" label="Personnel (cumul)" value={resume.personnel_jours} sub="Personnes × jours" /></Grid>
          {resume.par_type.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mr: 0.5 }}>Production par type :</Typography>
                {resume.par_type.map((t) => <Chip key={t.code} size="small" variant="outlined" label={<><b>{t.code}</b> · {t.quantite}</>} />)}
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <Panel flush>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>N°</TableCell><TableCell>Saisi par</TableCell>
                <TableCell align="right">PBA produits</TableCell><TableCell align="right">Personnel</TableCell><TableCell>Enregistré le</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id} hover sx={{ cursor: 'pointer' }} onClick={() => viewReport(report.id)}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{new Date(report.report_date).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono, fontSize: 13, color: 'text.secondary' }}>{reportNumber(report)}</TableCell>
                  <TableCell>
                    {report.employee_firstname && report.employee_name
                      ? `${report.employee_firstname} ${report.employee_name}`
                      : report.created_by_username || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{normalizeReport(report).totalPba}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{normalizeReport(report).totalPersonnel || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{new Date(report.created_at).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Voir"><IconButton size="small" aria-label="Voir le rapport" onClick={() => viewReport(report.id)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Modifier"><IconButton size="small" aria-label="Modifier le rapport" onClick={() => navigate(`/rapports/${report.id}/modifier`)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Supprimer"><IconButton size="small" aria-label="Supprimer le rapport" onClick={() => deleteReport(report.id)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>{loading ? 'Chargement…' : 'Aucun rapport sur cette période'}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pagination.pages} page={pagination.page} color="primary"
            onChange={(e, page) => setPagination((prev) => ({ ...prev, page }))} />
        </Box>
      )}

        {/* Détail du rapport */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>Rapport journalier de production</DialogTitle>
          <DialogContent dividers>
            {selectedReport && <ReportBody report={selectedReport} />}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
            <Button onClick={() => setOpenDialog(false)} sx={{ mr: 'auto' }}>Fermer</Button>
            {selectedReport && <Button startIcon={<EditOutlined />} onClick={() => navigate(`/rapports/${selectedReport.id}/modifier`)}>Modifier</Button>}
            {selectedReport && <ReportActions report={selectedReport} onInfo={setInfo} />}
          </DialogActions>
        </Dialog>
        <Snackbar open={!!info} autoHideDuration={3000} onClose={() => setInfo('')} message={info} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default ReportsHistory;